/**
 * Static content, types, and routing logic for the
 * "Give Something Into Stewardship" workflow.
 *
 * Language canon for this folder: plain spoken register, short sentences, no
 * dashes, no internal codes, "sample documents" never "templates", no
 * tax-outcome promises, and the designation vocabulary is
 * "place of stewardship observance".
 */

export type StormAnswer = 'no' | 'yes' | 'not_sure';
export type ThingKind =
  | 'vehicle'
  | 'home_land'
  | 'money'
  | 'securities'
  | 'crypto'
  | 'business'
  | 'equipment'
  | 'life_insurance';
export type VehicleJob = 'family' | 'mission' | 'not_sure';
export type LandUse = 'rental' | 'mission';

export interface ChoiceOption<T extends string> {
  value: T;
  label: string;
  helper: string;
}

export const ORIENTATION_KNOW =
  "Giving has three shapes, and the thing itself picks the shape. You can give the commitment: the thing stays in your name, and a short written commitment in the Association's records honors the conviction fully, with nothing else in your life changing. You can give the asset: title moves, and the asset's whole life moves with it, registration and insurance for a vehicle, occupancy papers for a home someone lives in. Or you can give the value: contribute money and let the entity buy what its work needs directly, in its own name from day one. Conviction decides that you give. The thing's job decides the shape.";

export const ORIENTATION_DO =
  'Answer the questions on the next screens. If you are not sure of an answer, tap Not sure and we will help you figure it out. The portal prepares only the papers your answers call for, and walks you through signing and filing them.';

export const ORIENTATION_SCHEDULE =
  'Most gifts ask nothing of you afterward. Where anything recurring exists, it folds into the annual review you already hold. Nothing new lives in your head.';

export const STORM_QUESTION =
  'One question before anything moves. Is there any lawsuit, claim, creditor demand, divorce, dispute, or bankruptcy, active or expected, anywhere near you or this asset?';

export const STORM_STOP_YES =
  'Nothing moves during a storm, no matter how sincere the giving. This is a hard rule that protects you and the Association. Talk with the team first.';

export const STORM_STOP_NOT_SURE =
  'If it might be there, we treat it as there for now. Nothing moves during a storm, and this rule protects you and the Association. Talk with the team and we will sort out what is real together.';

export const SEQUENCING_TEACHING =
  'Papers sign before anything moves. They carry the true date they are signed. There is no back dating here, and the portal will not offer it.';

export const STORM_OPTIONS: ChoiceOption<StormAnswer>[] = [
  { value: 'no', label: 'No', helper: 'Nothing like that is happening, and nothing like it is expected.' },
  { value: 'yes', label: 'Yes', helper: 'Something like that is active now, or we expect it.' },
  { value: 'not_sure', label: 'Not sure', helper: 'There may be something. We would rather ask than guess.' },
];

export const THING_OPTIONS: ChoiceOption<ThingKind>[] = [
  { value: 'vehicle', label: 'A vehicle', helper: 'A car, a truck, a van, a trailer, or anything else with a title.' },
  { value: 'home_land', label: 'A home or land', helper: 'A house, a rental, a lot, or buildings on land.' },
  { value: 'money', label: 'Money or a bank account', helper: 'Cash, a transfer, or the balance of a bank account.' },
  {
    value: 'securities',
    label: 'Stocks or investment accounts',
    helper: 'Shares, funds, bonds, or anything else held at a broker.',
  },
  { value: 'crypto', label: 'Crypto', helper: 'Coins or tokens held in a wallet or at an exchange.' },
  { value: 'business', label: 'A business or company', helper: 'A company you own, in whole or in part.' },
  {
    value: 'equipment',
    label: 'Metals, valuables, or other belongings',
    helper: 'Gold and silver, art, instruments, tools, gear, furniture, and the like.',
  },
  {
    value: 'life_insurance',
    label: 'A life insurance policy',
    helper: 'A policy on a life, whether term or one with cash value.',
  },
];

export const VEHICLE_JOB_OPTIONS: ChoiceOption<VehicleJob>[] = [
  { value: 'family', label: "Our family's daily life", helper: 'School runs, errands, getting to work, family trips.' },
  {
    value: 'mission',
    label: "The mission's actual work (a work truck, a delivery van, a vehicle that would exist even without us)",
    helper: 'The work needs this vehicle whether or not your household exists.',
  },
  { value: 'not_sure', label: 'Not sure', helper: 'It does some of both and you want help sorting it.' },
];

export const VEHICLE_NOT_SURE_HELPER =
  'Would a thoughtful outsider, seeing how it is actually used, call it a business vehicle? Substantial regular business use says mission. School runs and errands say family.';

export const VEHICLE_NOT_SURE_FOLLOWUP = 'Still not sure? Ask Stew from the Support tab and come back.';

export const VEHICLE_FAMILY_PLAIN =
  'Title stays yours. Insurance stays yours. Nothing changes at the DMV, and that is the design.';

export const VEHICLE_PRIVACY_STOP =
  'The privacy pattern is real and it has its own careful setup outside the main structure. Talk with the team before forming anything.';

export const HOME_LIVED_IN_TEACHING =
  "For the home you live in, the strongest shape usually keeps title in your name: the organization leases the rooms it actually uses at fair value, your household stays the owner, and no occupancy papers are needed. The Establish the Center and Occupancy workflow builds that whole arrangement, papers included. Moving your home's title instead is a real option with real consequences (county transfer taxes, in California the reassessment rules, your loan's fine print), and that path starts with the team so nothing surprises you.";

export const RENTAL_TEACHING =
  "Rentals live in their own property state company under the holding company, one property state per company. That keeps each property's risk with that property. If that company already exists, the portal prepares the giving papers now. If it does not exist yet, form it first and come back.";

export const MISSION_PROPERTY_TEACHING =
  'Land and buildings held for the work of the organization take the same giving papers, with the designation resolution added so the record says plainly that the place is a place of stewardship observance.';

export const MONEY_TEACHING =
  'Money is the simplest gift and it has its own short flow. Use the Record a Contribution workflow and it takes a few minutes.';

export const MONEY_ACCOUNTS_LINE =
  'Bank accounts themselves do not change hands either. The entity has its own accounts, and the money moves to them. The contribution flow records it.';

export const SECURITIES_TEACHING_ONE =
  "Accounts do not change hands; holdings do. The entity opens its own investment account, and the shares transfer into it through the broker's own gift or transfer paperwork. Your account stays yours and simply gets lighter.";

export const SECURITIES_TEACHING_TWO =
  'Order matters more here than anywhere: a position given before it is sold is given whole; a position sold first was your sale. If a sale is anywhere in the plan, pause and get the sequencing right before anything moves.';

export const SECURITIES_NO_ACCOUNT_STOP =
  'The entity needs its own investment account before anything can transfer into it. Open that account first, using the accounts guidance in the library, and come back here when it is open.';

export const SECURITIES_CLOSING_LINE =
  'Whether and how this shows up on your own return belongs to your licensed professional.';

export const CRYPTO_TEACHING =
  "Crypto moves wallet to wallet, and the receiving wallet must be the entity's own: its own hardware wallet, its own seed, set up before anything moves. Seeds are never shared and never written into any record.";

export const CRYPTO_NO_WALLET_STOP =
  'Set up the entity custody first: its own hardware wallet with its own seed, held by the entity. Nothing moves until that exists. Talk with the team and they will walk you through it.';

export const METALS_CUSTODY_NOTE =
  'Stored value belongs in storage the entity controls under its own name. If there is a vault account, a safe deposit box, or a storage unit, it should be in the exact legal name of the entity.';

export const LIFE_INSURANCE_TEACHING =
  "A policy can serve stewardship, and changing its owner or beneficiary runs through the insurance company's own forms with a licensed producer in the conversation, because policies have moving parts (loans, cash value, tax character) that deserve professional eyes before anything signs. The team sets this up with you.";

export const BUSINESS_TEACHING =
  'Bringing an existing company in is a real procedure with a review that runs first: guarantees, loans, leases, licenses, and consents all get checked before anything assigns, because finding them after is the expensive order. The team runs this with you.';

export const EQUIPMENT_VALUE_NOTE =
  'Where the value is substantial, attach a written value basis to the giving record: what it is, what it is worth, and how you arrived at that number.';

export const ENCUMBRANCE_LINES: string[] = [
  'Personal guarantees signed by any owner',
  'Secured debts and the collateral behind them',
  'Mortgages, and whether any of them carry due on sale language',
  'Leases, for space and for equipment',
  'Client and vendor contracts, and whether any need consent to assign',
  'Licenses and permits, and whether they follow the company or the person',
  'Insurance policies and who is named on them',
  'Merchant accounts and payment processing',
  'Banking, signers, and lines of credit',
  'State registrations and good standing',
  'Government records, filings, and identification numbers',
  'The storm screen: any lawsuit, claim, creditor demand, divorce, dispute, or bankruptcy',
  'Spousal consent where the ownership is marital property',
];

export interface GivingWorkflowData {
  storm?: StormAnswer;
  thing?: ThingKind;

  vehicle_job?: VehicleJob;
  vehicle_privacy_pattern?: boolean;
  vehicle_description?: string;
  vehicle_vin?: string;
  vehicle_driver?: string;

  land_lived_in?: 'yes' | 'no';
  land_use?: LandUse;
  land_llc_exists?: boolean;
  property_address?: string;
  property_county?: string;

  securities_description?: string;
  securities_institution?: string;
  securities_entity_account_exists?: 'yes' | 'no';

  crypto_description?: string;
  crypto_entity_wallet_exists?: 'yes' | 'no';

  equipment_description?: string;
  equipment_value?: string;
  equipment_custody?: string;

  giver_name?: string;
  receiving_entity?: string;
  spousal_consent_needed?: boolean;
  notes?: string;
}

export interface RouteResult {
  /** true when this branch is a teaching or hand-off screen with no papers */
  generatesNothing: boolean;
  /** short plain line shown on the review screen */
  summary: string;
  /** hard stop, nothing saves forward and no papers prepare */
  hardStop: boolean;
}

export function routeGiving(data: GivingWorkflowData): RouteResult {
  if (data.storm === 'yes' || data.storm === 'not_sure') {
    return { generatesNothing: true, hardStop: true, summary: 'Nothing moves right now. Talk with the team first.' };
  }
  if (!data.thing) {
    return { generatesNothing: true, hardStop: false, summary: 'Choose what the thing is.' };
  }
  if (data.thing === 'money') {
    return {
      generatesNothing: true,
      hardStop: false,
      summary: 'Money has its own short flow. Use the Record a Contribution workflow.',
    };
  }
  if (data.thing === 'life_insurance') {
    return {
      generatesNothing: true,
      hardStop: false,
      summary: 'No papers prepare here. The team sets this up with you and a licensed producer.',
    };
  }
  if (data.thing === 'securities') {
    if (data.securities_entity_account_exists !== 'yes') {
      return {
        generatesNothing: true,
        hardStop: false,
        summary: 'Open the investment account for the entity first, then come back.',
      };
    }
    return {
      generatesNothing: false,
      hardStop: false,
      summary:
        'Three papers prepare: the giving record for the holdings, the accepting resolution, and the securities transfer checklist.',
    };
  }
  if (data.thing === 'crypto') {
    if (data.crypto_entity_wallet_exists !== 'yes') {
      return {
        generatesNothing: true,
        hardStop: false,
        summary: 'Set up the wallet for the entity first, then come back.',
      };
    }
    return {
      generatesNothing: false,
      hardStop: false,
      summary:
        'Three papers prepare: the giving record for the digital assets, the accepting resolution, and the transfer checklist.',
    };
  }
  if (data.thing === 'business') {
    return {
      generatesNothing: false,
      hardStop: false,
      summary: 'One worksheet prepares here: the encumbrance review. The team runs the rest with you.',
    };
  }
  if (data.thing === 'vehicle') {
    if (data.vehicle_privacy_pattern) {
      return { generatesNothing: true, hardStop: true, summary: 'Talk with the team before forming anything.' };
    }
    if (data.vehicle_job === 'family') {
      return {
        generatesNothing: false,
        hardStop: false,
        summary: 'One page prepares: the vehicle stewardship commitment. Title and insurance stay yours.',
      };
    }
    if (data.vehicle_job === 'mission') {
      return {
        generatesNothing: false,
        hardStop: false,
        summary: 'Five papers prepare: the acquiring resolution, the giving record, the title move checklist, the use policy resolution, and a blank use log.',
      };
    }
    return { generatesNothing: true, hardStop: false, summary: 'Answer whose job the vehicle is.' };
  }
  if (data.thing === 'home_land') {
    if (data.land_lived_in === 'yes') {
      return {
        generatesNothing: true,
        hardStop: false,
        summary: 'No papers prepare here. The Establish the Center and Occupancy workflow builds that arrangement.',
      };
    }
    if (data.land_use === 'rental' && !data.land_llc_exists) {
      return {
        generatesNothing: true,
        hardStop: false,
        summary: 'Form the property state company first, then come back.',
      };
    }
    if (data.land_use === 'rental') {
      return {
        generatesNothing: false,
        hardStop: false,
        summary: 'Four papers prepare: the giving record, the authorizing resolution, the accepting resolution, and the title move checklist.',
      };
    }
    if (data.land_use === 'mission') {
      return {
        generatesNothing: false,
        hardStop: false,
        summary: 'Five papers prepare: the giving record, the authorizing resolution, the accepting resolution, the title move checklist, and the designation resolution.',
      };
    }
    return { generatesNothing: true, hardStop: false, summary: 'Answer what the property is for.' };
  }
  // equipment
  return {
    generatesNothing: false,
    hardStop: false,
    summary: 'Two papers prepare: the giving record and the accepting resolution.',
  };
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
